import { Button, Grid2, Paper, Snackbar, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../../../components/shared/CenteredLazyLoading";
import { Alert } from "../../../../../controls/alert";
import { useContextMenuMESF } from "../../../../../controls/contextMenu";
import { themeDXT } from "../../../../../css/themeMESF";
import { useQueryCacheStats } from "../../hooks/useQueryCacheStats";
import {
	Action,
	CacheConfiguration,
	QueryCacheConfigurationsResponse,
} from "../../models/CacheConfiguration";
import {
	getQueryCacheConfigurations,
	invalidateQueryCache,
} from "../../repositories/query-cache-repository";
import { DeleteCachedAction } from "../Delete/DeleteCachedAction";
import { QueryCacheUsage } from "../QueryCacheUsage/QueryCacheUsage";
import { UpsertCachedAction } from "../Upsert/UpsertCachedAction";
import { useQueryCacheOptionsFunctions } from "./customHooks/useQueryCacheOptionsFunctions";
import { useTableData } from "./customHooks/useTableData";

const useSearchQueryCacheConfigurations = () => {
	return useQuery<QueryCacheConfigurationsResponse, Error>({
		queryKey: ["queryCacheConfigurations"],
		queryFn: ({ signal }) => getQueryCacheConfigurations(signal),
	});
};

export const TableQueryCache = () => {
	const [modalUpsert, setModalUpsert] = useState(false);
	const [modalDelete, setModalDelete] = useState(false);
	const [configuration, setConfiguration] = useState<CacheConfiguration | null>(
		null,
	);
	const [actions, setActions] = useState<Action[]>([]);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [notification, setNotification] = useState("");
	const [error, setError] = useState("");

	const {
		data: rows,
		isLoading,
		isError,
		error: e,
		refetch,
	} = useSearchQueryCacheConfigurations();

	const configurations = rows?.configurations ?? [];

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
			suppressHeaderMenuButton: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
		};
	}, []);

	// Same query as the usage card above, served from React Query's cache, so the table can show
	// which configurations are actually holding data.
	const { data: stats, refetch: refetchStats } = useQueryCacheStats();

	const clearCache = useMutation({
		mutationFn: invalidateQueryCache,
		onSuccess: () => {
			setNotification("The cache was cleared for that action");
			// Without this the figures only catch up on the next 30s poll, and the button looks
			// like it did nothing.
			refetchStats();
		},
		onError: (err: Error) => setError(err.message),
	});

	const onClearCache = (row: CacheConfiguration) =>
		clearCache.mutate(row.ActionName);

	const { getMenuOptions } = useQueryCacheOptionsFunctions({
		setConfiguration,
		setShowUpsertModal: setModalUpsert,
		setShowDeleteModal: setModalDelete,
		onClearCache,
	});
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs } = useTableData({
		setConfiguration,
		setModalUpsert,
		setModalDelete,
		onClearCache,
		showContextMenu,
		cachedActions: stats?.actions,
	});

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (DefaultMenuItem | MenuItemDef)[] => {
		const data = params.node?.data;
		params.api.deselectAll();
		params.node?.setSelected(true);
		showContextMenu(event as any, data, "TableQueryCache");
		return [];
	};

	useEffect(() => {
		registerConfig({
			id: "TableQueryCache",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (rows?.actions) setActions(rows.actions);
	}, [rows]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Query Cache Config
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="body2" color="text.secondary">
						Actions listed here are answered from memory instead of the database
						until their TTL runs out. Anything not listed behaves as usual.
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<QueryCacheUsage />
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					{/* Leaves room for the usage card above and the button below, which the taller
					    grid was pushing off the bottom of the screen. */}
					<Grid2
						size={{ md: 12, xs: 12 }}
						component={Paper}
						style={{ height: "55vh" }}
					>
						<AgGridReact<CacheConfiguration>
							loading={isLoading}
							gridOptions={{
								theme: themeDXT,
							}}
							rowData={configurations}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							rowHeight={34}
							headerHeight={34}
							loadingOverlayComponent={CenteredLazyLoading}
							animateRows
							getContextMenuItems={(e: any) => getContextMenuItems(e)}
							getRowId={(params) => String(params.data.ActionId)}
							rowSelection="single"
							onRowDoubleClicked={(event) => {
								setConfiguration(event.data as CacheConfiguration);
								setModalUpsert(true);
							}}
							onGridReady={(params) => setGridApi(params.api)}
						/>
					</Grid2>
					<Grid2 container justifyContent="flex-end">
						<Grid2 size={{ md: 2, xs: 12 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => {
									setConfiguration(null);
									setModalUpsert(true);
								}}
								fullWidth
							>
								NEW CACHED ACTION
							</Button>
						</Grid2>
					</Grid2>
				</Grid2>
			</Grid2>

			<UpsertCachedAction
				show={modalUpsert}
				configuration={configuration}
				actions={actions}
				configuredActionIds={configurations.map((c) => c.ActionId)}
				onHide={(shouldUpdate) => {
					setModalUpsert(false);
					setConfiguration(null);
					if (shouldUpdate) refetch();
				}}
				suffixTitle="Query Cache Config"
			/>
			<DeleteCachedAction
				show={modalDelete}
				configuration={configuration}
				onHide={(shouldUpdate) => {
					setModalDelete(false);
					setConfiguration(null);
					if (shouldUpdate) refetch();
				}}
				suffixTitle="Query Cache Config"
			/>
			<Snackbar
				open={!!notification}
				autoHideDuration={2500}
				onClose={() => setNotification("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={() => setNotification("")}>
					{notification}
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
